import '/modules/common/css/ui/box-plot.css';
import '/modules/common/css/ui/box-plot.responsive.css';
import {arrayLast, mean, quantile, stddev, variance} from "../util";
import * as $ from "jquery";
import {AbstractSimpleStatsShower} from "./AbstractSimpleStatsShower";

/**
 * A simple stats shower for numerical samples that implements a small box plot chart
 * @extends AbstractSimpleStatsShower
 */
class BoxPlot extends AbstractSimpleStatsShower {

    /**
     *
     * @param decimalPlaces {number} (integer) The nr of decimal places to show
     * @param data {Array<number|string>} The data to show stats for
     * @param id {string} Unique id of this UI element
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param runtimeConfig {RuntimeConfig} The runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     **/
    constructor(decimalPlaces, data, id, translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig) {
        super(data, id, translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig);
        this.decimalPlaces = decimalPlaces;
    }

    getClassName(){
        return "BoxPlot";
    }

    _generateUIElements() {
        const element = $(
            '<div class="box-plot-container">' +
            '   <div class="box-plot">' +
            '       <div class="hor-line"></div>' +
            '       <div class="min"></div>' +
            '       <div class="max"></div>' +
            '       <div class="min-max"></div>' +
            '       <div class="box"></div>' +
            '       <div class="med"></div>' +
            '   </div>' +
            '</div>'
        );

        if(this.data.length===0){
            this.logError("Empty data array");
        } else {

            this.min = this.data[0];
            this.max = arrayLast(this.data);
            this.totalDelta = this.max - this.min;

            this.avg = mean(this.data);
            this.stdev = stddev(this.data);
            this.vari = variance(this.data);

            this.med = quantile(this.data, .5)
            this.boxMin = quantile(this.data, .25)
            this.boxMax = quantile(this.data, .75)
            this.boxDelta = this.boxMax - this.boxMin
            this.IQR = this.boxDelta;

            this.plotMin = Math.max(this.boxMin - this.IQR * 1.5, this.min !== undefined ? this.min : Number.MIN_VALUE);
            this.plotMax = Math.min(this.boxMax + this.IQR * 1.5, this.max !== undefined ? this.max : Number.MAX_VALUE);
            this.plotDelta = this.plotMax - this.plotMin;

            let localPlotMin = this.plotMin;
            let localPlotMax = this.plotMax;
            let localPlotDelta = this.plotDelta;
            let error = false;

            if (localPlotMin < this.min) {
                error = true;
                this.logError('(p25 - IQR * 1.5) < min ');
            }

            if (localPlotMin > this.boxMin) {
                error = true;
                this.logError('(p25 - IQR * 1.5) > p25 ');
            }

            if (localPlotMax > this.max) {
                error = true;
                this.logError('(p75 + IQR * 1.5) > max ');
            }

            if (localPlotMax < this.boxMax) {
                error = true;
                this.logError('(p75 + IQR * 1.5) < p75 ')
            }

            if (error) {
                localPlotMax = this.max;
                localPlotMin = this.min;
                localPlotDelta = localPlotMax - localPlotMin;
                $('div.min', element).hide();
                $('div.max', element).hide();
                $('div.box', element).hide();
                $('div.min-max', element).addClass('error');
            }


            const horLineWidth = (localPlotDelta) / this.totalDelta * 100;
            const horLineLeft = (localPlotMin - this.min) / this.totalDelta * 100;
            $('div.min-max', element).css('width', horLineWidth + '%');
            $('div.min-max', element).css('left', horLineLeft + '%');
            $('div.hor-line', element).css('width', horLineWidth + '%');
            $('div.hor-line', element).css('left', horLineLeft + '%');

            const medLeft = (this.med - this.min) / this.totalDelta * 100;
            $('div.med', element).css('left', 'calc(' + medLeft + '% - ' + 1 + 'px)');

            $('div.box', element).css('left', ((this.boxMin - this.min) / this.totalDelta * 100) + '%');
            $('div.box', element).css('width', (this.boxDelta / this.totalDelta * 100) + '%');

            $('.stats-elements', this.uiElement.addClass('box-plot')).append(element);
        }
    }

    _generateLegendElements(legendElem) {
        legendElem.addClass('box-plot').append($(
            '<table>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.min') + '</label></td><td class="value min-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.q1') + '</label></td><td class="value q1-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.med') + '</label></td><td class="value med-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.q3') + '</label></td><td class="value q3-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.max') + '</label></td><td class="value max-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.iqr') + '</label></td><td class="value iqr-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.mean') + '</label></td><td class="value mean-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.stddev') + '</label></td><td class="value stddev-val"></td></tr>' +
            '<tr><td class="label"><label>' + this.translator.translate('tool.charts.boxplot.var') + '</label></td><td class="value var-val"></td></tr>' +
            '</table>'
        ));
        $('td.min-val', legendElem).html(this.min.toFixed(this.decimalPlaces));
        $('td.q1-val', legendElem).html(this.boxMin.toFixed(this.decimalPlaces));
        $('td.med-val', legendElem).html(this.med.toFixed(this.decimalPlaces));
        $('td.q3-val', legendElem).html(this.boxMax.toFixed(this.decimalPlaces));
        $('td.max-val', legendElem).html(this.max.toFixed(this.decimalPlaces));
        $('td.iqr-val', legendElem).html(this.IQR.toFixed(this.decimalPlaces));
        $('td.mean-val', legendElem).html(this.avg.toFixed(this.decimalPlaces));
        $('td.stddev-val', legendElem).html(this.stdev.toFixed(this.decimalPlaces));
        $('td.var-val', legendElem).html(this.vari.toFixed(this.decimalPlaces));
    }
}

export {BoxPlot}